// Substitution Rules API using direct fetch

/// <reference lib="dom" />

import { handleApiCall, ValidationError } from './errors'
import { getAccessToken } from './auth-token'
import type { SubstitutionRules } from '@timetable/types'

// Get environment variables
const SUPABASE_URL = (import.meta as any).env?.VITE_SUPABASE_URL as string
const SUPABASE_ANON_KEY = (import.meta as any).env?.VITE_SUPABASE_ANON_KEY as string

if (!SUPABASE_URL || !SUPABASE_ANON_KEY) {
  throw new Error('Missing Supabase environment variables')
}

// Default substitution rules (Requirement 10.2)
const DEFAULT_RULES: Omit<SubstitutionRules, 'school_id'> = {
  min_free_periods: 2,
  max_subs_per_day: 2,
  max_subs_per_week: 5,
  min_free_periods_for_second_sub: 5,
  total_periods_per_day: 8,
  lunch_start: '12:00',
  lunch_end: '13:30',
  prefer_subject_match: true,
  prefer_class_association: true,
  avoid_consecutive: true,
  prefer_class_teacher_for_supervision: true,
  eligible_teacher_type_ids: null,
  assignment_mode: 'auto',
  notification_channel: 'email',
  auto_generate_enabled: true,
  auto_generate_time: '17:00',
  lock_timeout_minutes: 15,
  notification_sender_name: null,
  notification_sender_email: null,
}

// ============================================================================
// Get Substitution Rules
// ============================================================================

/**
 * Fetch substitution rules for a school.
 * If no row exists, returns an object with all default values.
 */
export async function getSubstitutionRules(schoolId: string): Promise<SubstitutionRules> {
  return handleApiCall(async () => {
    const accessToken = getAccessToken()
    if (!accessToken) throw new Error('Not authenticated')

    const params = new URLSearchParams()
    params.append('select', '*')
    params.append('school_id', `eq.${schoolId}`)
    params.append('limit', '1')

    const response = await fetch(
      `${SUPABASE_URL}/rest/v1/substitution_rules?${params}`,
      {
        headers: {
          'apikey': SUPABASE_ANON_KEY,
          'Authorization': `Bearer ${accessToken}`,
          'Content-Type': 'application/json',
        },
      }
    )

    if (!response.ok) {
      const error = await response.json().catch(() => ({}))
      throw Object.assign(new Error(error.message || 'Failed to fetch substitution rules'), error)
    }

    const rows: SubstitutionRules[] = await response.json()

    if (!rows || rows.length === 0) {
      // No row exists — return defaults
      return {
        school_id: schoolId,
        ...DEFAULT_RULES,
      }
    }

    return rows[0]
  })
}

// ============================================================================
// Save Substitution Rules
// ============================================================================

/**
 * Validate and upsert substitution rules for a school.
 * Throws ValidationError if any constraint is violated.
 */
export async function saveSubstitutionRules(
  schoolId: string,
  rules: SubstitutionRules
): Promise<void> {
  // Validation 1: min_free_periods must be less than total_periods_per_day
  if (rules.min_free_periods >= rules.total_periods_per_day) {
    throw new ValidationError(
      'min_free_periods must be less than total_periods_per_day',
      { min_free_periods: 'Must be less than total periods per day' }
    )
  }

  // Validation 2: max_subs_per_day must be <= total_periods_per_day
  if (rules.max_subs_per_day > rules.total_periods_per_day) {
    throw new ValidationError(
      'max_subs_per_day must be less than or equal to total_periods_per_day',
      { max_subs_per_day: 'Must be less than or equal to total periods per day' }
    )
  }

  // Validation 3: lunch_start must be before lunch_end (compare as time strings)
  if (rules.lunch_start >= rules.lunch_end) {
    throw new ValidationError(
      'lunch_start must be before lunch_end',
      { lunch_start: 'Lunch start time must be before lunch end time' }
    )
  }

  return handleApiCall(async () => {
    const accessToken = getAccessToken()
    if (!accessToken) throw new Error('Not authenticated');

    const baseHeaders = {
      'apikey': SUPABASE_ANON_KEY,
      'Authorization': `Bearer ${accessToken}`,
      'Content-Type': 'application/json',
    };

    // Step 1: Check if a record already exists for this school
    const checkResponse = await fetch(
      `${SUPABASE_URL}/rest/v1/substitution_rules?select=id&school_id=eq.${schoolId}&limit=1`,
      { headers: baseHeaders }
    );

    if (!checkResponse.ok) {
      const error = await checkResponse.json().catch(() => ({}));
      throw Object.assign(new Error(error.message || 'Failed to check existing rules'), error);
    }

    const existing: { id: string }[] = await checkResponse.json();
    const recordExists = existing && existing.length > 0;

    const requestBody = { ...rules, school_id: schoolId };

    let response: Response;

    if (recordExists) {
      // Update the existing record
      response = await fetch(
        `${SUPABASE_URL}/rest/v1/substitution_rules?school_id=eq.${schoolId}`,
        {
          method: 'PATCH',
          headers: baseHeaders,
          body: JSON.stringify(requestBody),
        }
      );
    } else {
      // Insert a new record
      response = await fetch(
        `${SUPABASE_URL}/rest/v1/substitution_rules`,
        {
          method: 'POST',
          headers: baseHeaders,
          body: JSON.stringify(requestBody),
        }
      );
    }

    if (!response.ok) {
      const errorBody = await response.json().catch(async () => ({
        message: await response.text().catch(() => 'Unknown error'),
      }));
      throw Object.assign(
        new Error(errorBody.message || 'Failed to save substitution rules'),
        errorBody
      );
    }
  })
}
